import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { ArrowRight, MapPin, Users, Heart } from "lucide-react";
import api, { API } from "../lib/api";

export default function MarketLanding() {
  const { marketId } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    api.get(`/markets/${marketId}/public`)
      .then((r) => {
        setData(r.data);
        const name = r.data.market.display_name;
        document.title = `Singles in ${name} · DateBid`;
        let meta = document.querySelector('meta[name="description"]');
        if (!meta) { meta = document.createElement("meta"); meta.name = "description"; document.head.appendChild(meta); }
        meta.content = `Meet ${r.data.member_count}+ singles in ${name} on DateBid — make a date offer and get noticed. Join free.`;
        const setOg = (p, c) => { let m = document.querySelector(`meta[property="${p}"]`); if (!m) { m = document.createElement("meta"); m.setAttribute("property", p); document.head.appendChild(m); } m.content = c; };
        setOg("og:title", `Singles in ${name} · DateBid`);
        setOg("og:description", `Meet ${r.data.member_count}+ singles in ${name} on DateBid.`);
      })
      .catch(() => setNotFound(true));
  }, [marketId]);

  if (notFound) return (
    <div className="min-h-screen flex items-center justify-center text-center" style={{ background: "#0C0A0A" }}>
      <div><h1 className="font-serif text-3xl text-[#FBF8F3]">Market not found</h1>
        <button onClick={() => navigate("/")} className="mt-4 text-[#D4AF37] underline">Back to DateBid</button></div>
    </div>
  );
  if (!data) return <div className="min-h-screen flex items-center justify-center text-[#9E978E]" style={{ background: "#0C0A0A" }}>Loading…</div>;

  const { market, member_count, teaser } = data;
  return (
    <div className="db-grain min-h-screen" style={{ background: "radial-gradient(900px 500px at 50% -10%, rgba(117,26,50,0.25), transparent 60%), #0C0A0A" }} data-testid="market-landing">
      <header className="flex items-center justify-between px-6 py-5 max-w-6xl mx-auto">
        <button onClick={() => navigate("/")} className="font-serif text-2xl text-[#FBF8F3]">Date<span className="text-[#D4AF37]">Bid</span></button>
        <button data-testid="market-landing-signin" onClick={() => navigate("/auth")} className="text-sm text-[#D8D2C9] hover:text-[#D4AF37]">Sign in</button>
      </header>

      <section className="max-w-4xl mx-auto px-6 pt-10 pb-6 text-center db-fade-up">
        <div className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.2em] text-[#D4AF37] mb-4"><MapPin size={14} /> {market.type?.replace(/_/g, " ")} · {[market.region, market.country].filter(Boolean).join(", ")}</div>
        <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl text-[#FBF8F3] leading-tight">Singles in {market.display_name}</h1>
        <p className="mt-4 text-base text-[#C9A96E] flex items-center justify-center gap-2"><Users size={16} /> {member_count} {member_count === 1 ? "member" : "members"} ready to be asked out</p>
        <p className="mt-3 text-sm text-[#9E978E] max-w-xl mx-auto">On DateBid, generous people make monetary date offers to the members they want to meet in {market.display_name}. Create your profile free and start getting noticed.</p>
        <button data-testid="market-landing-join" onClick={() => navigate("/auth?mode=register")} className="db-offer-btn mt-7 rounded-full px-8 py-3.5 text-sm font-semibold inline-flex items-center gap-2">Join to connect <ArrowRight size={16} /></button>
      </section>

      <section className="max-w-5xl mx-auto px-6 py-8">
        {teaser.length === 0 ? (
          <p className="text-center text-[#9E978E]">Be the first to join DateBid in {market.display_name}.</p>
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4">
            {teaser.map((t, i) => (
              <div key={i} data-testid={`market-teaser-${i}`} className="relative rounded-2xl overflow-hidden border border-white/8 bg-[#141113] aspect-[3/4] group">
                {t.photo_id ? (
                  <img src={`${API}/photos/${t.photo_id}/raw`} alt={t.first_name} className="w-full h-full object-cover" />
                ) : (
                  <div className="w-full h-full flex items-center justify-center text-[#751A32]"><Heart size={40} /></div>
                )}
                <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/85 to-transparent p-3">
                  <div className="text-[#FBF8F3] text-sm font-medium">{t.first_name}{t.age ? `, ${t.age}` : ""}</div>
                </div>
              </div>
            ))}
          </div>
        )}
        <div className="text-center mt-10">
          <p className="text-sm text-[#9E978E] mb-4">Sign up free to see everyone in {market.display_name} and send your first date offer.</p>
          <button onClick={() => navigate("/auth?mode=register")} className="db-offer-btn rounded-full px-8 py-3.5 text-sm font-semibold inline-flex items-center gap-2">Create your free profile <ArrowRight size={16} /></button>
        </div>
      </section>

      <footer className="text-center py-8 text-xs text-[#6E6861]">DateBid · Premium dating marketplace · Members 18+</footer>
    </div>
  );
}
